import { Box, Typography, Paper, Chip, Link } from "@mui/material";
import Grid from "@mui/material/Grid";
import { Email, Phone, Language, Star } from "@mui/icons-material";
import type { Company } from "@/types";

interface CompanyDetailHeaderProps {
  company: Company;
}

function normalizeUrl(url: string): string {
  return url.startsWith("http") ? url : `https://${url}`;
}

export default function CompanyDetailHeader({ company }: CompanyDetailHeaderProps) {
  return (
    <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: 2,
            bgcolor: "action.hover",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: 28,
            fontWeight: 800,
          }}
        >
          {company.displayName.charAt(0)}
        </Box>
        <Box
          sx={{
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                }}
              >
                {company.displayName}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                }}
              >
                OIB: {company.oib}
              </Typography>
            </Box>
            {company.overallRating && (
              <Chip
                icon={<Star />}
                label={company.overallRating.toFixed(1)}
                color="primary"
                size="medium"
              />
            )}
          </Box>
        </Box>
      </Box>
      <Grid container spacing={2} sx={{ mt: 3 }}>
        {company.emails.length > 0 && (
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Email color="primary" fontSize="small" />
              <Typography variant="body2">{company.emails.join(", ")}</Typography>
            </Box>
          </Grid>
        )}
        {company.phones.length > 0 && (
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Phone color="primary" fontSize="small" />
              <Typography variant="body2">{company.phones.join(", ")}</Typography>
            </Box>
          </Grid>
        )}
        {company.urls.length > 0 && (
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Language color="primary" fontSize="small" />
              {company.urls.map((url, i) => (
                <Link
                  key={i}
                  href={normalizeUrl(url)}
                  target="_blank"
                  rel="noopener"
                  variant="body2"
                >
                  {url}
                </Link>
              ))}
            </Box>
          </Grid>
        )}
      </Grid>
      {company.description && (
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mt: 2,
          }}
        >
          {company.description}
        </Typography>
      )}
    </Paper>
  );
}
